import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";

import { decodeJwtClaims } from "@/lib/jwt";

const ACCESS_TOKEN_KEY = "eslsync_token";
const REFRESH_TOKEN_KEY = "eslsync_refresh_token";
const IMPERSONATING_TENANT_KEY = "eslsync_impersonating_tenant_id";

// --------------------------------------------------------------------------
// Token storage
// --------------------------------------------------------------------------

export function getToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

export function setTokens(access: string, refresh: string): void {
  setToken(access);
  setRefreshToken(refresh);
}

export function clearToken(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(IMPERSONATING_TENANT_KEY);
}

// --------------------------------------------------------------------------
// Impersonation persistence — super-admin koji je impersonate-ovao tenant-a
// treba da zadrži taj kontekst preko refresh access token-a. Refresh endpoint
// na backendu po dizajnu vraća super-admin scope (bez `impersonating` claim-a),
// pa se ovde čuva target tenant id i auto-restore-uje posle svakog refresh-a.
// --------------------------------------------------------------------------

export function getImpersonatingTenantId(): number | null {
  const raw = localStorage.getItem(IMPERSONATING_TENANT_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function setImpersonatingTenantId(id: number): void {
  localStorage.setItem(IMPERSONATING_TENANT_KEY, String(id));
}

export function clearImpersonatingTenantId(): void {
  localStorage.removeItem(IMPERSONATING_TENANT_KEY);
}

// --------------------------------------------------------------------------
// Axios instance
// --------------------------------------------------------------------------

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? "",
  timeout: 15_000,
});

// Request interceptor — automatski dodaj access token
apiClient.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --------------------------------------------------------------------------
// Refresh-on-401 interceptor sa serijalizacijom (jedan refresh poziv ako više
// concurrent zahteva pukne 401 istovremeno).
// --------------------------------------------------------------------------

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

let refreshPromise: Promise<string> | null = null;

async function runRefresh(): Promise<string> {
  const refresh = getRefreshToken();
  if (!refresh) throw new Error("no refresh token");
  const baseURL = apiClient.defaults.baseURL ?? "";
  const { data } = await axios.post<{
    access_token: string;
    refresh_token: string;
  }>(
    `${baseURL}/api/auth/refresh`,
    { refresh_token: refresh },
    { timeout: 15_000 },
  );
  setTokens(data.access_token, data.refresh_token);

  // Auto-restore impersonation: backend refresh vraća super-admin scope, pa ako
  // je super-admin imao aktivnu impersonation sesiju, ovde je transparentno
  // obnavljamo. Bez ovoga, super-admin bi tiho ispao u svoj globalni view i
  // gledao podatke drugog tenant-a (banner bi nestao, što je UX katastrofa).
  const targetTenantId = getImpersonatingTenantId();
  if (targetTenantId !== null) {
    const claims = decodeJwtClaims(data.access_token);
    const stillImpersonating =
      claims?.impersonating === true && claims.tenant_id === targetTenantId;
    if (claims?.is_super_admin === true && !stillImpersonating) {
      try {
        const impResp = await axios.post<{
          access_token: string;
          tenant_id: number;
          tenant_slug: string;
        }>(
          `${baseURL}/api/admin/tenants/${targetTenantId}/impersonate`,
          {},
          {
            headers: { Authorization: `Bearer ${data.access_token}` },
            timeout: 15_000,
          },
        );
        setToken(impResp.data.access_token);
        return impResp.data.access_token;
      } catch {
        // Re-impersonate fail-uje (tenant deaktiviran, obrisan, ili nemamo prava
        // više) → očisti memo i nastavi kao super-admin u globalnom view-u.
        // Banner će nestati, što je sad eksplicitno (ne tiho).
        clearImpersonatingTenantId();
      }
    }
  }

  return data.access_token;
}

function getOrStartRefresh(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = runRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

apiClient.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;
    const status = error.response?.status;
    const url = config?.url ?? "";

    // Refresh sam ne sme da retry-uje sebe (večita petlja)
    const isAuthEndpoint =
      url.endsWith("/api/auth/refresh") ||
      url.endsWith("/api/auth/login") ||
      url.endsWith("/api/auth/logout");

    if (status === 401 && config && !config._retried && !isAuthEndpoint && getRefreshToken()) {
      config._retried = true;
      try {
        const newAccess = await getOrStartRefresh();
        config.headers.Authorization = `Bearer ${newAccess}`;
        return apiClient.request(config);
      } catch {
        // Refresh fail-ovao → hard logout
        clearToken();
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
    }

    if (status === 401 && !isAuthEndpoint) {
      // Bez refresh tokena, ili retry već probao i pukao → logout
      clearToken();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);
